import { useEffect, useEffectEvent, type RefObject } from 'react';
import { useMap } from 'react-leaflet';
import type { VehiclePosition } from '../..';
import { PanelVisibilityHelper } from '../../helpers/panel-visibility.helper';

export default function MapPanelLayout({ panelRef, active, selectedVehicleId, vehicle }: {
  panelRef: RefObject<HTMLDivElement | null>;
  active: boolean;
  selectedVehicleId: string | undefined;
  vehicle: VehiclePosition | undefined;
}) {
  const map = useMap();

  const revealCoveredVehicle = useEffectEvent((panelRect: DOMRect, mapRect: DOMRect, sidePanel: boolean) => {
    if (vehicle?.latitude == null || vehicle.longitude == null) return;
    const position: [number, number] = [vehicle.latitude, vehicle.longitude];
    const point = map.latLngToContainerPoint(position);
    if (!PanelVisibilityHelper.isCovered({ x: mapRect.left + point.x, y: mapRect.top + point.y }, panelRect)) return;
    map.panInside(position, {
      paddingTopLeft: [sidePanel ? panelRect.width + 28 : 28, 28],
      paddingBottomRight: [28, sidePanel ? 28 : panelRect.height + 28],
      animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  });

  useEffect(() => {
    const container = map.getContainer();
    const attribution = container.querySelector('.leaflet-control-attribution');
    const controls = container.querySelector('.leaflet-bottom.leaflet-right');
    if (!attribution || !controls) return;
    const layout = container.parentElement;
    const updateAttributionHeight = () => {
      layout?.style.setProperty('--map-attribution-height', `${attribution.getBoundingClientRect().height}px`);
      layout?.style.setProperty('--map-controls-height', `${controls.getBoundingClientRect().height + 10}px`);
    };
    const observer = new ResizeObserver(updateAttributionHeight);
    observer.observe(attribution);
    observer.observe(controls);
    updateAttributionHeight();
    return () => {
      observer.disconnect();
      layout?.style.removeProperty('--map-attribution-height');
      layout?.style.removeProperty('--map-controls-height');
    };
  }, [map]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!active || !panel) return;
    const container = map.getContainer();
    const layout = panel.parentElement;
    const updateControls = () => {
      const panelRect = panel.getBoundingClientRect();
      const mapRect = container.getBoundingClientRect();
      const sidePanel = panelRect.width < mapRect.width;
      layout?.style.setProperty('--map-panel-width', `${sidePanel ? panelRect.width : 0}px`);
      layout?.style.setProperty('--map-panel-height', `${sidePanel ? 0 : panelRect.height}px`);
      revealCoveredVehicle(panelRect, mapRect, sidePanel);
    };
    const observer = new ResizeObserver(updateControls);
    observer.observe(panel);
    observer.observe(container);
    updateControls();
    return () => {
      observer.disconnect();
      layout?.style.removeProperty('--map-panel-width');
      layout?.style.removeProperty('--map-panel-height');
    };
  }, [map, panelRef, active, selectedVehicleId]);

  return null;
}
